import { Link } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { FaCrown } from "react-icons/fa";

import { Avatar } from "MVVM/Components/AvatarV2";
import { TUserGroup } from "MVVM/@types";
import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { ILeaderboardEntry } from "../../../Competitions/@types";
import { RankAccent, rankAccent } from "../../../Competitions/utils";

interface CompactLeaderboardProps {
  entries: ILeaderboardEntry[];
  loading: boolean;
  competitionId: string;
}

export const CompactLeaderboard = ({
  entries,
  loading,
  competitionId,
}: CompactLeaderboardProps) => {
  const theme = useTheme() as { colors: Record<string, string> };

  if (loading) {
    return (
      <List>
        {Array.from({ length: 5 }).map((_, i) => (
          <LoadingSkeleton key={i} style={{ height: 48, margin: "0 0 8px" }} />
        ))}
      </List>
    );
  }

  if (entries.length === 0) {
    return (
      <Empty>
        <EmptyTitle>No entries yet</EmptyTitle>
        <EmptyBody>Be the first to enter.</EmptyBody>
      </Empty>
    );
  }

  return (
    <List>
      {entries.map((entry) => {
        const accent = rankAccent(entry.rank, theme);
        const isFirst = entry.rank === 1;
        return (
          <Row
            key={entry.userId}
            accent={accent}
            to={{
              pathname: `/competitions/${competitionId}/contestant/${entry.userId}`,
              state: { entry },
            }}
          >
            <Rank accent={accent}>
              {isFirst ? <FaCrown size={13} /> : entry.rank ?? "—"}
            </Rank>
            <Avatar
              src={entry.user?.avatarUrl || undefined}
              size="sm"
              userGroup={entry.user?.userGroup?.type as TUserGroup}
            />
            <Name highlight={!!accent}>
              {entry.user?.username ?? "Unknown"}
            </Name>
            <ScoreCol>
              <ScoreValue accent={accent}>
                {Math.round(entry.score).toLocaleString()}
              </ScoreValue>
              <ScoreUnit>pts</ScoreUnit>
            </ScoreCol>
          </Row>
        );
      })}
    </List>
  );
};

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 10px;
`;

const Row = styled(Link)<{ accent: RankAccent | null }>`
  display: grid;
  grid-template-columns: 22px 44px 1fr auto;
  align-items: center;
  gap: 8px 14px;
  padding: 8px 12px;
  border-radius: 10px;
  text-decoration: none;
  background: ${({ accent }) => accent?.bg ?? "transparent"};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ accent }) =>
      accent?.bgHover ?? "rgba(247, 252, 253, 0.05)"};
  }
`;

const Rank = styled.span<{ accent: RankAccent | null }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: ${({ accent, theme }) => accent?.color ?? theme.colors.shade400};
`;

const Name = styled.span<{ highlight: boolean }>`
  font-size: 14px;
  font-weight: ${({ highlight }) => (highlight ? 700 : 600)};
  color: ${({ theme }) => theme.colors.ice};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ScoreCol = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
`;

const ScoreValue = styled.span<{ accent: RankAccent | null }>`
  font-size: 14px;
  font-weight: 800;
  color: ${({ accent, theme }) => accent?.color ?? theme.colors.ice};
  font-variant-numeric: tabular-nums;
`;

const ScoreUnit = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.shade400};
`;

const Empty = styled.div`
  padding: 36px 16px;
  text-align: center;
`;

const EmptyTitle = styled.div`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const EmptyBody = styled.div`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
`;
